import { Moon, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useSleepTrends, SleepTrendPoint } from '@/hooks/use-health-trends';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';

interface SleepTrendsChartProps {
  clientId: string;
  days?: number;
  className?: string;
}

function formatDate(dateStr: string) {
  try {
    return format(parseISO(dateStr), 'MMM d');
  } catch {
    return dateStr;
  }
}

function getQualityColor(quality: string | null): string {
  switch (quality) {
    case 'excellent':
      return 'text-success';
    case 'good':
      return 'text-info';
    case 'fair':
      return 'text-warning';
    case 'poor':
      return 'text-destructive';
    default:
      return 'text-muted-foreground';
  }
}

function calculateTrend(data: SleepTrendPoint[]): 'improving' | 'declining' | 'stable' {
  if (data.length < 7) return 'stable';

  const midpoint = Math.floor(data.length / 2);
  const firstHalf = data.slice(0, midpoint);
  const secondHalf = data.slice(midpoint);

  const firstAvg =
    firstHalf.reduce((sum, d) => sum + (d.duration || 0), 0) / firstHalf.length;
  const secondAvg =
    secondHalf.reduce((sum, d) => sum + (d.duration || 0), 0) / secondHalf.length;

  if (secondAvg > firstAvg * 1.05) return 'improving';
  if (secondAvg < firstAvg * 0.95) return 'declining';
  return 'stable';
}

export function SleepTrendsChart({ clientId, days = 30, className }: SleepTrendsChartProps) {
  const { data, loading, error } = useSleepTrends(clientId, days);

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="chart-height-sm w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Moon className="h-5 w-5 text-chart-4" />
            Sleep Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center chart-height-sm text-muted-foreground">
            <Moon className="h-12 w-12 mb-2 opacity-20" />
            <p className="text-sm">No sleep data available</p>
            <p className="text-xs">Connect a health device to track sleep</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const trend = calculateTrend(data);
  const avgSleep = data.reduce((sum, d) => sum + (d.duration || 0), 0) / data.length;

  // Transform data for chart
  const chartData = data.map((d) => ({
    date: formatDate(d.date),
    total: d.duration,
    deep: d.deep_sleep,
    rem: d.rem_sleep,
    light: d.light_sleep,
    quality: d.quality,
  }));

  return (
    <Card className={cn('border', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Moon className="h-5 w-5 text-chart-4" />
            Sleep Trends
          </span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              {trend === 'improving' && <TrendingUp className="h-4 w-4 text-success" />}
              {trend === 'declining' && <TrendingDown className="h-4 w-4 text-destructive" />}
              {trend === 'stable' && <Minus className="h-4 w-4 text-muted-foreground" />}
              <span className="capitalize">{trend}</span>
            </div>
            <span className="text-xl font-bold text-chart-4">
              {avgSleep.toFixed(1)}h avg
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div role="img" aria-label="Sleep duration trends over time">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              domain={[0, 12]}
              tickFormatter={(value) => `${value}h`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value: number, name: string) => [
                `${value?.toFixed(1) || 0}h`,
                name.charAt(0).toUpperCase() + name.slice(1),
              ]}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
            />
            <Area
              type="monotone"
              dataKey="deep"
              stackId="1"
              stroke="#4338ca"
              fill="#4338ca"
              fillOpacity={0.8}
              name="Deep"
            />
            <Area
              type="monotone"
              dataKey="rem"
              stackId="1"
              stroke="#7c3aed"
              fill="#7c3aed"
              fillOpacity={0.6}
              name="REM"
            />
            <Area
              type="monotone"
              dataKey="light"
              stackId="1"
              stroke="#a78bfa"
              fill="#a78bfa"
              fillOpacity={0.4}
              name="Light"
            />
          </AreaChart>
        </ResponsiveContainer>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-4 gap-2 mt-4 text-center text-xs">
          <div className="bg-muted/50 rounded p-2">
            <div className="text-muted-foreground">Total Avg</div>
            <div className="font-semibold">{avgSleep.toFixed(1)}h</div>
          </div>
          <div className="bg-chart-4/10 rounded p-2">
            <div className="text-muted-foreground">Deep Avg</div>
            <div className="font-semibold text-chart-4">
              {(data.reduce((s, d) => s + (d.deep_sleep || 0), 0) / data.length).toFixed(1)}h
            </div>
          </div>
          <div className="bg-chart-4/10 rounded p-2">
            <div className="text-muted-foreground">REM Avg</div>
            <div className="font-semibold text-chart-4">
              {(data.reduce((s, d) => s + (d.rem_sleep || 0), 0) / data.length).toFixed(1)}h
            </div>
          </div>
          <div className="bg-chart-4/10 rounded p-2">
            <div className="text-muted-foreground">Light Avg</div>
            <div className="font-semibold text-chart-4">
              {(data.reduce((s, d) => s + (d.light_sleep || 0), 0) / data.length).toFixed(1)}h
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
